import { color, font, gradient, motion, radius } from './tokens.js';
/** Flattens nested colors to Tailwind names: surface.0 → "surface-0", line.DEFAULT → "line". */
export function flattenColors(tree, prefix = '') {
    return Object.entries(tree).flatMap(([key, value]) => {
        const name = key === 'DEFAULT' ? prefix : prefix ? `${prefix}-${key}` : key;
        return typeof value === 'string' ? [[name, value]] : flattenColors(value, name);
    });
}
function rgb(hex, alpha) {
    const n = Number.parseInt(hex.slice(1), 16);
    return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${alpha})`;
}
const bezier = ([a, b, c, d]) => `cubic-bezier(${a}, ${b}, ${c}, ${d})`;
const ms = (value) => `${value}ms`;
const stack = (f) => [`'${f.family}'`, ...f.fallback].join(', ');
const { duration: d, easing: e } = motion;
const [g1, g2, g3] = gradient.live;
/** Animation utilities (animate-*) and the keyframes they use. */
const animations = [
    ['ring-spin', `nc-spin ${ms(d.ring)} linear infinite`],
    ['border-spin', `nc-spin ${ms(d.border)} linear infinite`],
    ['speak-glow', `nc-glow ${ms(d.glow)} ${bezier(e.easeOut)} infinite`],
    ['speak-glow-lg', `nc-glow-lg ${ms(d.glow)} ${bezier(e.easeOut)} infinite`],
    ['live-blink', `nc-blink ${ms(d.blink)} ${bezier(e.inOut)} infinite`],
    ['chip-pulse', `nc-chip ${ms(d.chip)} ${bezier(e.easeOut)} infinite`],
    ['enter', `nc-enter ${ms(d.enter)} ${bezier(e.out)} both`],
    ['typing', `nc-dot ${ms(d.typing)} infinite`],
    ['shake', `nc-shake ${ms(d.shake)} ${bezier(e.shake)} both`],
];
const keyframes = `
  @keyframes nc-spin { to { --nc-angle: 360deg; } }
  @keyframes nc-glow {
    0% { box-shadow: 0 0 0 0 ${rgb(color.pink[500], 0.42)}; }
    100% { box-shadow: 0 0 0 ${motion.glowSpreadPx.sm}px ${rgb(color.pink[500], 0)}; }
  }
  @keyframes nc-glow-lg {
    0% { box-shadow: 0 0 0 0 ${rgb(color.pink[500], 0.32)}; }
    100% { box-shadow: 0 0 0 ${motion.glowSpreadPx.lg}px ${rgb(color.pink[500], 0)}; }
  }
  @keyframes nc-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
  @keyframes nc-chip {
    0% { box-shadow: 0 0 0 0 ${rgb(color.brand[500], 0.6)}; }
    100% { box-shadow: 0 0 0 8px ${rgb(color.brand[500], 0)}; }
  }
  @keyframes nc-enter {
    from { opacity: 0; transform: translateY(${motion.enterOffsetPx}px); }
    to { opacity: 1; transform: none; }
  }
  @keyframes nc-dot {
    0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-3px); }
  }
  @keyframes nc-shake {
    10%, 90% { transform: translateX(-1px); }
    20%, 80% { transform: translateX(2px); }
    30%, 50%, 70% { transform: translateX(-3px); }
    40%, 60% { transform: translateX(3px); }
  }`;
/**
 * Tailwind v4 theme. `@theme static` keeps every variable in the output even when no
 * utility uses it, so hand-written component CSS can rely on var(--color-…) too.
 * The default Tailwind palette and radius scale are cleared: only NovaChat tokens exist.
 */
export function buildThemeCss() {
    const colors = flattenColors(color)
        .map(([name, value]) => `  --color-${name}: ${value};`)
        .join('\n');
    const radii = Object.entries(radius)
        .map(([name, value]) => `  --radius-${name}: ${value}px;`)
        .join('\n');
    const anims = animations.map(([name, value]) => `  --animate-${name}: ${value};`).join('\n');
    return `/* Generated by @novachat/design-tokens. Edit src/tokens.ts, not this file. */

@property --nc-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@theme static {
  --color-*: initial;
${colors}

  --font-heading: ${stack(font.heading)};
  --font-sans: ${stack(font.body)};
  --font-mono: ${stack(font.mono)};

  --radius-*: initial;
${radii}

  --ease-out-soft: ${bezier(e.out)};
  --ease-shake: ${bezier(e.shake)};

${anims}
${keyframes}
}

:root {
  color-scheme: dark;
  /* Rotating border/ring fill. Pair with animate-ring-spin or animate-border-spin. */
  --nc-gradient-live: conic-gradient(from var(--nc-angle), ${g1}, ${g2}, ${g3}, ${g1});
  --nc-duration-hover: ${ms(d.hover)};
  --nc-hover-lift: -${motion.hoverLiftPx}px;
  --nc-press-scale: ${motion.pressScale};
}

/* Motion only explains state. With "reduce motion" on, everything stops. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
`;
}
//# sourceMappingURL=css.js.map